import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject, throwError, timer } from 'rxjs';
import { SignalrService } from '../signalr.service';
import {
  KorisniciClient,
  LoginType,
  RefreshTokenRequest,
  SubscriptionPackage,
  TokensClient,
} from '@kodit/core/data-api';
import { Store } from '@ngrx/store';
import { deleteAllFilters } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.actions';
import { user } from '@angular/fire/auth';
import { PosthogService } from '../analytics/posthog.service';
import { TokenService } from './token.service';
import { AlertService } from '../alert.service';
import { catchError, finalize, shareReplay, switchMap, timeout } from 'rxjs/operators';

const REFRESH_TIMEOUT_MS = 15000;
const REFRESH_RETRY_DELAY_MS = 300;

/** Backend Succeeded=false (concurrency race ili revoked sesija) — ne mora znaciti nevazecu sesiju, vidi refreshTokens(). */
class RefreshNotSucceededError extends Error {}

export const SESSION_ID_NAME = 'eKompanija_userSessionId';
export const TOKEN_NAME = 'eKompanija_token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _refreshTokenName = 'eKompanija_refreshToken';

  constructor(
    private _jwtHelper: JwtHelperService,
    private _router: Router,
    private _korisniciClient: KorisniciClient,
    private _signalRService: SignalrService,
    private _tokenService: TokenService,
    private _storage: Store,
    private _posthogService: PosthogService,
    private _tokensClient: TokensClient,
    private _alertService: AlertService,
  ) {}

  /**
   * Jedini choke-point za refresh tokena u aplikaciji — i HTTP interceptor i AuthGuard pozivaju refreshTokens().
   * Ako je refresh vec u toku, svi pozivaoci dele isti in-flight Observable (nema paralelnih refresh poziva
   * sa istim starim refresh tokenom, koji bi na backendu izazvali concurrency race na rotaciji).
   */
  private _refreshInFlight$: Observable<string> | null = null;

  public isUserAuthenticated = (): boolean => {
    return this.getToken && !this._jwtHelper.isTokenExpired(this.getToken);
  };

  /**
   * @param showSessionExpiredMessage prikazuje upozorenje da je sesija istekla — koristi se samo kad je refresh
   * tokena stvarno propao (nevazeca/revoked sesija), ne kad je logout eksplicitna korisnicka akcija.
   */
  public logout(showSessionExpiredMessage: boolean = false) {
    if (!!localStorage.getItem(SESSION_ID_NAME)) {
      this._korisniciClient
        .logoutUser(localStorage.getItem(SESSION_ID_NAME))
        .subscribe(() => {});
    }
    this._tokenService.setShowLogoutDialog = false;
    this.removeTokens();
    this._storage.dispatch(deleteAllFilters());
    if (showSessionExpiredMessage) {
      this._alertService.addWarnMsg('Sesija je istekla, prijavite se ponovo.');
    }
    this._router.navigate(['/autentikacija', 'prijava']).then();
  }

  /**
   * Vraca da li je user authenticated
   */
  appInitialized$ = new BehaviorSubject<boolean>(false);

  set setAppInitialized(value: boolean) {
    this.appInitialized$.next(value);
  }

  get getAppInitialized() {
    return this.appInitialized$.asObservable();
  }

  getAppInitializedValue() {
    return this.appInitialized$.value;
  }

  get getUserClaims(): string[] {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT?.Permission;
  }

  get getCurrentTenant(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT?.tenantKey;
  }

  get getCurrentTenantName(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT?.tenantName;
  }

  get getUserRoles(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT?.[
      'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
    ];
  }

  get getNazivPoslovneJedinice(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT?.['poslovnaJedinica'];
  }

  get getUserData(): { email: string; fullName: string } {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return {
      email:
        decodedJWT?.[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ],
      fullName: decodedJWT?.fullName,
    };
  }

  get getUserPackages(): string[] {
    const packages = this._jwtHelper.decodeToken(this.getToken).packages;
    if (!packages) {
      return [];
    }

    return [...packages];
  }

  get getToken(): string {
    return localStorage.getItem(TOKEN_NAME);
  }

  get getRefreshToken(): string {
    return localStorage.getItem(this._refreshTokenName);
  }

  get getSessionId(): string {
    return localStorage.getItem(SESSION_ID_NAME);
  }

  get getIsTrial(): boolean {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT.isTrial.toLowerCase() === 'true';
  }

  get getIsTenantActive(): boolean {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    return decodedJWT.isActive.toLowerCase() === 'true';
  }

  get getSubDaysLeftText(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    const subEnd = decodedJWT.subscriptionEnd;
    if (!subEnd) return 'Još 0 dana probnog perioda';

    const end = new Date(subEnd);
    const now = new Date();

    // Compare date-only (ignore time)
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffMs = endDate.getTime() - nowDate.getTime();
    let days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (days < 0) days = 0;

    const lastTwo = days % 100;
    const lastOne = days % 10;

    const dayWord =
      lastTwo >= 11 && lastTwo <= 14
        ? 'dana'
        : lastOne === 1
          ? 'dan'
          : 'dana';

    return `Još ${days} ${dayWord} probnog perioda`;
  }

  get getSubDaysLeft(): number {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    const subEnd = decodedJWT.subscriptionEnd;
    if (!subEnd) return 0;

    const end = new Date(subEnd);
    const now = new Date();

    // Compare date-only (ignore time)
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffMs = endDate.getTime() - nowDate.getTime();
    let days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (days < 0) days = 0;

    return days;
  }

  get getSubExpires(): string {
    const decodedJWT = this._jwtHelper.decodeToken(this.getToken);
    const date = new Date(decodedJWT.subscriptionEnd);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;

  }

  shouldUpdatePassword$ = new BehaviorSubject<boolean>(false);

  get getShouldUpdatePassword() {
    return this.shouldUpdatePassword$.asObservable();
  }

  set setShouldUpdatePassword(data: boolean) {
    this.shouldUpdatePassword$.next(data);
  }

  /**
   * @param restartSignalR Rusi i ponovo gradi SignalR konekciju - neophodno samo kad se menja tenant
   * (login, promena firme), jer se tada menja i SignalR grupa kojoj klijent pripada. Za tihi token-refresh
   * istog tenanta (refreshTokens/tryRefreshingTokens) ovo NIJE potrebno: accessTokenFactory vec cita svez
   * token iz localStorage pri svakom (re)connect pokusaju, pa rusenje konekcije samo pravi nepotreban
   * prozor "gluvoce" u kom se izgube SignalR poruke (npr. progress bar za SEF sinhronizaciju).
   */
  setTokens(token: string, refreshToken: string, sessionId: string, restartSignalR: boolean = false) {
    if (restartSignalR) {
      this._signalRService.closeConnection();
    }
    localStorage.setItem(TOKEN_NAME, token);
    localStorage.setItem(this._refreshTokenName, refreshToken);
    localStorage.setItem(SESSION_ID_NAME, sessionId);
    if (restartSignalR) {
      this.startSignalR();
    }
    this._posthogService.identify({
      id: this.getUserData.email,
      email: this.getUserData.email,
      companyId: this.getCurrentTenant,
      plan: this.getUserPackages.join(','),
    });
    //this._afterLoginService.getDataAfterLogin();
  }

  removeTokens() {
    // stop signalR connection
    this._signalRService.closeConnection();
    localStorage.removeItem(TOKEN_NAME);
    localStorage.removeItem(this._refreshTokenName);
    localStorage.removeItem(SESSION_ID_NAME);
    this.setAppInitialized = false;
  }

  /**
   * U slucaju da dode do greske, da mozemo da prikazemo odgovarajucu poruku
   */
  userChanged$ = new BehaviorSubject<boolean>(false);

  get getUserChanged() {
    return this.userChanged$.asObservable();
  }

  set setUserChanged(data: boolean) {
    this.userChanged$.next(data);
  }

  _packagesChanged$ = new Subject<void>();

  get getPackagesChanged() {
    return this._packagesChanged$.asObservable();
  }

  notifyPackagesChanged() {
    this._packagesChanged$.next();
  }

  // This method can be called a couple of different ways
  // *hasClaim="'claimType'"  // Assumes claimValue is true
  // *hasClaim="'claimType:value'" // Compares claimValue to value
  // *hasClaim="['claimType1','claimType2:value','claimType3']"
  hasClaim(claimValue: string | string[]): boolean {
    if (!this.isUserAuthenticated()) {
      return false;
    }

    if (this.getUserClaims.includes('ALL')) {
      return true;
    }

    let userHasClaim: boolean = false;

    // See if an array of values was passed in.
    if (typeof claimValue === 'string') {
      userHasClaim = this.isClaimValid(claimValue);
    } else {
      let claims: string[] = claimValue;
      if (claims) {
        for (let index = 0; index < claims.length; index++) {
          userHasClaim = this.isClaimValid(claims[index]);
          // If one is successful, then let them in
          if (userHasClaim) {
            break;
          }
        }
      }
    }
    return userHasClaim;
  }

  private isClaimValid(claimValue: string): boolean {
    let ret: boolean = false;
    const regEx = new RegExp(`\\b${claimValue}\\b`);

    for (let i = 0; i < this.getUserClaims?.length; ++i) {
      if (regEx.test(this.getUserClaims[i])) {
        ret = true;
        break;
      }
    }
    return ret;
  }

  hasRole(requiredRoles: string | string[]): boolean {
    if (!this.isUserAuthenticated()) {
      return false;
    }

    return this.isInRole(requiredRoles);
  }

  /**
   * start signalR connection
   */
  public startSignalR() {
    this._signalRService.startConnection(this.getCurrentTenant, this.getToken);
  }

  hasPackage(requiredPackages: string | string[]): boolean {
    if (!this.isUserAuthenticated()) {
      return false;
    }

    if (this.getUserPackages.includes('ROOT')) {
      return true;
    }

    return this.containsPackages(requiredPackages);
  }

  hasInfoZgrada(): boolean {
    return this.containsPackages(
      SubscriptionPackage[SubscriptionPackage.INFO_ZGRADA],
    );
  }

  private containsPackages(requiredPackages: string | string[]): boolean {
    const packages = this.getUserPackages;
    if (typeof requiredPackages === 'string') {
      return packages.includes(requiredPackages);
    } else {
      for (let index = 0; index < requiredPackages.length; index++) {
        // If one is successful, then let them in!
        if (packages.includes(requiredPackages[index])) {
          return true;
        }
      }
    }
    return false;
  }

  private isInRole(requiredRoles: string | string[]): boolean {
    const roles = this.getUserRoles;
    if (typeof requiredRoles === 'string') {
      return roles.includes(requiredRoles);
    } else {
      for (let index = 0; index < requiredRoles.length; index++) {
        // If one is successful, then let them in
        if (roles.includes(requiredRoles[index])) {
          return true;
        }
      }
    }
    return false;
  }

  hasAllOtherPackagesExcept(excludedPackages: string | string[]): boolean {
    const packages = this.getUserPackages;

    if (packages.includes('ROOT')) {
      return true;
    }

    const excluded =
      typeof excludedPackages === 'string' ? [excludedPackages] : excludedPackages;
    return packages.some((p) => !excluded.includes(p));
  }

  refreshTokens(): Observable<string> {
    if (!this.getCurrentTenant) {
      return of(null);
    }

    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    const refresh$ = this.performRefresh(true).pipe(
      finalize(() => {
        this._refreshInFlight$ = null;
      }),
      // Bez shareReplay je ovo cold Observable — svaki subscriber (svaki paralelni 401) bi
      // pokrenuo SOPSTVENI HTTP poziv umesto da dele jedan, poništavajući svrhu ovog choke-pointa.
      shareReplay(1),
    );

    this._refreshInFlight$ = refresh$;
    return refresh$;
  }

  /**
   * @param allowRetry dozvoljava jedan retry za tranzijentne greske (mrezni hiccup/timeout ili backend
   * Succeeded=false zbog concurrency race-a) pre nego sto se donese konacna odluka.
   */
  private performRefresh(allowRetry: boolean): Observable<string> {
    return this._tokensClient
      .refresh(
        new RefreshTokenRequest({
          token: this.getToken,
          refreshToken: this.getRefreshToken,
          sessionId: this.getSessionId,
          loginType: LoginType.WEB,
        }),
        this.getCurrentTenant,
      )
      .pipe(
        timeout(REFRESH_TIMEOUT_MS),
        switchMap((response) => {
          if (!response.succeeded || !response.data) {
            throw new RefreshNotSucceededError(response.messages?.join(' '));
          }
          const { token, refreshToken, sessionId } = response.data;
          this.setTokens(token, refreshToken, sessionId);
          return of(token);
        }),
        catchError((error) => {
          const isNetworkOrTimeout =
            (error as { status?: number })?.status === 0 ||
            error?.name === 'TimeoutError';

          if (isNetworkOrTimeout) {
            // Mrezni problem, ne signal da je sesija nevazeca — ne odjavljivati korisnika.
            return throwError(() => error);
          }

          if (error instanceof RefreshNotSucceededError && allowRetry) {
            return timer(REFRESH_RETRY_DELAY_MS).pipe(
              switchMap(() => this.performRefresh(false)),
            );
          }

          // Stvarni 401 (nevazeci/istekli refresh token), ili race koji ni retry nije razresio.
          this.logout(true);
          return of(null);
        }),
      );
  }
}